import { SxProps, Theme } from '@mui/material/styles';
import { useStore } from '../components/state/zustand-store';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import ContentCopy from '@mui/icons-material/ContentCopy';

interface TextHoverCopyProps {
  value: React.ReactNode;
  valueType: string;
  clipboardValue: string;
  wrapperSx?: SxProps<Theme>;
  stackSx?: SxProps<Theme>;
  textSx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
}

export default function TextHoverCopy({
  value,
  clipboardValue,
  valueType,
  wrapperSx,
  stackSx,
  textSx,
  iconSx,
}: TextHoverCopyProps) {
  const { setSnackbar } = useStore();
  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(clipboardValue);
        setSnackbar({
          open: true,
          severity: 'info',
          message: `${valueType} has been copied to clipboard.`,
        });
      }}
      sx={
        [
          (theme: Theme) => ({
            '&:active': {
              bgcolor:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[500]
                  : theme.palette.grey[900],
            },
            '&:hover': {
              bgcolor:
                theme.palette.mode === 'light'
                  ? theme.palette.grey[100]
                  : theme.palette.grey[900],
            },
            borderRadius: 4,
            cursor: 'pointer',
            width: 'max-content',
            textAlign: 'left',
            '&:hover .copy-icon': {
              opacity: 1,
              visibility: 'visible',
            },
          }),
          wrapperSx,
        ].filter(Boolean) as SxProps<Theme>
      }
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={
          [
            {
              width: '100%',
              pl: 1,
            },
            stackSx,
          ].filter(Boolean) as SxProps<Theme>
        }
      >
        <Typography
          sx={
            [
              {
                fontWeight: 300,
                fontSize: 14,
                flex: 1,
              },
              textSx,
            ].filter(Boolean) as SxProps<Theme>
          }
        >
          {value}
        </Typography>
        <IconButton
          className="copy-icon"
          size="small"
          sx={
            [
              {
                opacity: 0,
                transition: 'opacity 0.3s ease',
                visibility: 'hidden',
                width: 20,
                height: 20,
                minWidth: 20,
                minHeight: 20,
              },
              iconSx,
            ].filter(Boolean) as SxProps<Theme>
          }
        >
          <ContentCopy fontSize="inherit" />
        </IconButton>
      </Stack>
    </ButtonBase>
  );
}
